import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import EmptyState from './EmptyState';
import UserAvatar from './UserAvatar';
import TasksCalendarView, { getTaskScheduleKey } from './TasksCalendarView';
import TaskBillingStatusBadges from './TaskBillingStatusBadges';
import { formatDate } from '../utils/locale';
import { canAccessFeature, isCompanyAdmin, isCompanyEmployee } from '../utils/planAccess';
import { getTaskDisplayStatusClass } from '../utils/taskStatus';
import { formatTaskDisplayStatus, formatTaskPriority } from '../utils/taskLabels';
import { canClaimTask, getTaskAssigneeLabel } from '../utils/taskClaim';
import { cycleColumnSort, emptySort, sortTasksByColumn } from '../utils/taskListSort';
import { BILLING_FILTERS, matchesBillingFilter } from '../utils/taskBilling';

function getChecklistCounts(task) {
  let checklist = [];
  if (task.checklist) {
    if (Array.isArray(task.checklist)) {
      checklist = task.checklist;
    } else if (typeof task.checklist === 'string') {
      try {
        checklist = JSON.parse(task.checklist);
      } catch (e) {
        checklist = [];
      }
    }
  }
  const normalizedChecklist = checklist.map((item) => {
    if (typeof item === 'string') {
      return { text: item, checked: false };
    }
    return { text: item.text || item, checked: item.checked || false };
  });
  return {
    checkedCount: normalizedChecklist.filter((item) => item.checked).length,
    totalCount: normalizedChecklist.length
  };
}

function sectionCountLabel(t, count) {
  return count === 1
    ? t('tasks:sectionTaskCountOne')
    : t('tasks:sectionTaskCount', { count });
}

function TaskListSection({
  user,
  drafts,
  sortedTodayTasks,
  sortedUpcomingTasks,
  sortedCompletedTasks,
  allCompletedTasks,
  removedTasks = [],
  includeRemoved = false,
  upcomingViewMode,
  setUpcomingViewMode,
  calendarMonth,
  setCalendarMonth,
  selectedDay,
  setSelectedDay,
  onRowClick,
  onClaim,
  onOpenCreateModal,
  onCreateForDay,
  onNavigateDrafts
}) {
  const { t, i18n } = useTranslation(['tasks', 'common']);
  const navigate = useNavigate();
  const showWorkGroups = canAccessFeature(user, 'taskWorkGroups');
  const showBillingControls = isCompanyAdmin(user);
  const [billingFilter, setBillingFilter] = useState('all');
  const [sectionSorting, setSectionSorting] = useState({
    today: emptySort(),
    upcoming: emptySort(),
    completed: emptySort(),
    removed: emptySort()
  });

  const calendarSourceTasks = useMemo(
    () => [...sortedUpcomingTasks, ...sortedTodayTasks],
    [sortedUpcomingTasks, sortedTodayTasks]
  );

  const todayTasks = useMemo(() => {
    const { column, direction } = sectionSorting.today;
    return sortTasksByColumn(sortedTodayTasks, column, direction);
  }, [sortedTodayTasks, sectionSorting.today]);

  const upcomingTasks = useMemo(() => {
    const { column, direction } = sectionSorting.upcoming;
    return sortTasksByColumn(sortedUpcomingTasks, column, direction);
  }, [sortedUpcomingTasks, sectionSorting.upcoming]);

  const completedTasks = useMemo(() => {
    const { column, direction } = sectionSorting.completed;
    const filtered = showBillingControls
      ? sortedCompletedTasks.filter((task) => matchesBillingFilter(task, billingFilter))
      : sortedCompletedTasks;
    return sortTasksByColumn(filtered, column, direction);
  }, [sortedCompletedTasks, sectionSorting.completed, showBillingControls, billingFilter]);

  const sortedRemovedTasks = useMemo(() => {
    const { column, direction } = sectionSorting.removed;
    return sortTasksByColumn(removedTasks, column, direction);
  }, [removedTasks, sectionSorting.removed]);

  const handleSort = (sectionKey, column) => {
    setSectionSorting((prev) => ({
      ...prev,
      [sectionKey]: cycleColumnSort(prev[sectionKey], column)
    }));
  };

  const getTaskDisplayMeta = (task) => {
    const hasAssignedUsers = task.assigned_users && task.assigned_users.length > 0;
    const { checkedCount, totalCount } = getChecklistCounts(task);
    const locationLabel = task.location_name || task.location;
    const scheduleKey = getTaskScheduleKey(task);
    const scheduleLabel = scheduleKey
      ? formatDate(`${scheduleKey}T12:00:00`, i18n.language, { day: 'numeric', month: 'short' })
      : null;
    const assigneeLabel = getTaskAssigneeLabel(task);
    return { hasAssignedUsers, checkedCount, totalCount, locationLabel, scheduleLabel, assigneeLabel };
  };

  const renderClaimButton = (task) => (
    <button
      type="button"
      className="btn btn-primary btn-sm"
      onClick={(e) => {
        e.stopPropagation();
        // Claiming changes status/assignee; clear those sorts so the row stays put (#393).
        setSectionSorting((prev) => {
          const todaySort = prev.today || emptySort();
          if (todaySort.column === 'status' || todaySort.column === 'assignedTo') {
            return { ...prev, today: emptySort() };
          }
          return prev;
        });
        onClaim(task.id);
      }}
    >
      <span className="material-symbols-outlined" aria-hidden="true">handshake</span>
      {t('tasks:claim')}
    </button>
  );

  const renderSortableHeader = (sectionKey, column, label) => {
    const sortConfig = sectionSorting[sectionKey] || emptySort();
    const isSorted = sortConfig.column === column;
    const sortDirection = isSorted ? sortConfig.direction : null;
    const ariaSort = sortDirection === 'asc'
      ? 'ascending'
      : sortDirection === 'desc'
        ? 'descending'
        : undefined;

    return (
      <th
        className="task-sortable-header"
        scope="col"
        aria-sort={ariaSort}
        tabIndex={0}
        title={t('tasks:sortColumnAria', { column: label })}
        onClick={() => handleSort(sectionKey, column)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSort(sectionKey, column);
          }
        }}
      >
        <div className="task-sortable-header-inner">
          <span>{label}</span>
          {sortDirection === 'asc' && (
            <span className="material-symbols-outlined task-sort-icon" aria-hidden="true">
              arrow_upward
            </span>
          )}
          {sortDirection === 'desc' && (
            <span className="material-symbols-outlined task-sort-icon" aria-hidden="true">
              arrow_downward
            </span>
          )}
        </div>
      </th>
    );
  };

  const renderTaskCard = (task, { allowClaim = true } = {}) => {
    const {
      hasAssignedUsers,
      checkedCount,
      totalCount,
      locationLabel,
      scheduleLabel,
      assigneeLabel
    } = getTaskDisplayMeta(task);
    const claimable = allowClaim && canClaimTask(user, task);

    return (
      <article
        key={task.id}
        className={`tasks-redesign-card tasks-redesign-card--${(task.status || 'default').replace(/_/g, '-')}`}
        onClick={() => onRowClick(task.id)}
      >
        <div className="tasks-redesign-card-body">
          <div className="tasks-redesign-card-top">
            <h3 className="tasks-redesign-card-title">{task.title}</h3>
            <span className={getTaskDisplayStatusClass(task)}>
              {formatTaskDisplayStatus(task, t)}
            </span>
            <TaskBillingStatusBadges task={task} />
          </div>
          <div className="tasks-redesign-card-meta">
            {locationLabel && (
              <span className="tasks-redesign-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">location_on</span>
                {locationLabel}
              </span>
            )}
            {showWorkGroups && task.work_group_name && (
              <span className="tasks-redesign-meta-item" title={task.work_group_name}>
                <span className="material-symbols-outlined" aria-hidden="true">group</span>
                {task.work_group_name}
              </span>
            )}
            {scheduleLabel && (
              <span className="tasks-redesign-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">calendar_today</span>
                {scheduleLabel}
              </span>
            )}
            {task.priority && (
              <span className="tasks-redesign-meta-item">
                <span className={`tasks-priority-dot tasks-priority-dot--${task.priority}`} aria-hidden="true" />
                {formatTaskPriority(task.priority, t)}
              </span>
            )}
            {totalCount > 0 && (
              <span className="tasks-redesign-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">checklist</span>
                {checkedCount}/{totalCount}
              </span>
            )}
            {assigneeLabel && (
              <span className="tasks-redesign-meta-item">
                <span className="material-symbols-outlined" aria-hidden="true">person</span>
                {assigneeLabel}
              </span>
            )}
          </div>
          {claimable && (
            <div className="tasks-redesign-card-actions" onClick={(e) => e.stopPropagation()}>
              {renderClaimButton(task)}
            </div>
          )}
          {!claimable && hasAssignedUsers && (
            <div className="tasks-redesign-assignees" onClick={(e) => e.stopPropagation()}>
              {task.assigned_users.slice(0, 3).map((assignedUser, idx) => (
                <UserAvatar
                  key={idx}
                  user={{
                    first_name: assignedUser.firstName,
                    last_name: assignedUser.lastName,
                    profile_image_url: assignedUser.profile_image_url
                  }}
                  size={24}
                />
              ))}
            </div>
          )}
        </div>
      </article>
    );
  };

  const renderTaskTable = (tasks, sectionKey, { allowClaim = true } = {}) => (
    <div className="tasks-list-table-wrap tasks-list-table-desktop">
      <table className="table tasks-list-table">
        <thead>
          <tr>
            {renderSortableHeader(sectionKey, 'title', t('tasks:columnTitle'))}
            {renderSortableHeader(sectionKey, 'location', t('tasks:columnLocation'))}
            {renderSortableHeader(sectionKey, 'status', t('tasks:columnStatus'))}
            {showWorkGroups && renderSortableHeader(sectionKey, 'workGroup', t('tasks:columnWorkGroup'))}
            {renderSortableHeader(sectionKey, 'scheduled', t('tasks:columnScheduled'))}
            {renderSortableHeader(sectionKey, 'assignedTo', t('tasks:columnAssignedTo'))}
            <th scope="col">{t('tasks:columnActions')}</th>
          </tr>
        </thead>
        <tbody>
          {tasks.map((task) => {
            const { hasAssignedUsers, locationLabel, scheduleLabel, assigneeLabel } = getTaskDisplayMeta(task);
            const claimable = allowClaim && canClaimTask(user, task);

            return (
              <tr
                key={task.id}
                className="tasks-list-table-row"
                onClick={() => onRowClick(task.id)}
              >
                <td className="tasks-list-table-title">{task.title}</td>
                <td>{locationLabel || t('tasks:emptyCell')}</td>
                <td>
                  <div className="tasks-list-status-cell">
                    <span className={getTaskDisplayStatusClass(task)}>
                      {formatTaskDisplayStatus(task, t)}
                    </span>
                    <TaskBillingStatusBadges task={task} />
                  </div>
                </td>
                {showWorkGroups && (
                  <td className="tasks-list-work-group-cell" title={task.work_group_name || undefined}>
                    {task.work_group_name || t('tasks:emptyCell')}
                  </td>
                )}
                <td>{scheduleLabel || t('tasks:emptyCell')}</td>
                <td>
                  {hasAssignedUsers ? (
                    <div className="tasks-list-table-assignees">
                      {task.assigned_users.slice(0, 3).map((assignedUser, idx) => (
                        <UserAvatar
                          key={idx}
                          user={{
                            first_name: assignedUser.firstName,
                            last_name: assignedUser.lastName,
                            profile_image_url: assignedUser.profile_image_url
                          }}
                          size={24}
                        />
                      ))}
                      {assigneeLabel && (
                        <span className="tasks-list-table-assignee-name">{assigneeLabel}</span>
                      )}
                    </div>
                  ) : (
                    assigneeLabel || t('tasks:emptyCell')
                  )}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  {claimable ? renderClaimButton(task) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  const renderTaskList = (tasks, sectionKey, options = {}) => (
    <>
      <div className="tasks-card-grid tasks-list-cards-mobile">
        {tasks.map((task) => renderTaskCard(task, options))}
      </div>
      {renderTaskTable(tasks, sectionKey, options)}
    </>
  );

  return (
    <>
      {drafts.length > 0 && (
        <button
          type="button"
          className="tasks-drafts-entry"
          onClick={onNavigateDrafts}
        >
          <span className="tasks-drafts-entry-icon" aria-hidden="true">
            <span className="material-symbols-outlined">draft_orders</span>
          </span>
          <span className="tasks-drafts-entry-text">
            <span className="tasks-drafts-entry-title">
              {isCompanyEmployee(user)
                ? t('tasks:myDrafts', { count: drafts.length })
                : t('tasks:drafts', { count: drafts.length })}
            </span>
            <span className="tasks-drafts-entry-subtitle">
              {isCompanyEmployee(user)
                ? t('tasks:draftsEntrySubtitleEmployee')
                : t('tasks:draftsEntrySubtitle')}
            </span>
          </span>
          <span className="material-symbols-outlined tasks-drafts-entry-chevron" aria-hidden="true">
            chevron_right
          </span>
        </button>
      )}

      <section
        id="tasks-section-today"
        className="tasks-section"
        tabIndex={-1}
        aria-labelledby="tasks-section-today-heading"
      >
        <div className="tasks-section-header">
          <div>
            <h2 id="tasks-section-today-heading" className="tasks-section-heading">
              {t('tasks:todayOverdue')}
            </h2>
            <p className="tasks-section-count">{sectionCountLabel(t, sortedTodayTasks.length)}</p>
          </div>
        </div>
        {sortedTodayTasks.length > 0 ? (
          renderTaskList(todayTasks, 'today')
        ) : (
          <EmptyState
            icon="task_alt"
            title={t('tasks:noActiveTasksTitle')}
            description={t('tasks:noActiveTasksDescription')}
            actionLabel={isCompanyAdmin(user) ? t('tasks:createTask') : undefined}
            onAction={isCompanyAdmin(user) ? onOpenCreateModal : undefined}
          />
        )}
      </section>

      <hr className="tasks-section-divider" aria-hidden="true" />

      <section
        id="tasks-section-upcoming"
        className="tasks-section"
        tabIndex={-1}
        aria-labelledby="tasks-section-upcoming-heading"
      >
        <div className="tasks-section-header tasks-section-header--with-toggle">
          <div>
            <h2 id="tasks-section-upcoming-heading" className="tasks-section-heading">
              {t('tasks:upcoming')}
            </h2>
            <p className="tasks-section-count">{sectionCountLabel(t, sortedUpcomingTasks.length)}</p>
          </div>
          <div className="tasks-view-toggle" role="group" aria-label={t('tasks:upcoming')}>
            <button
              type="button"
              className={`tasks-view-toggle-btn${upcomingViewMode === 'list' ? ' is-active' : ''}`}
              onClick={() => setUpcomingViewMode('list')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">view_list</span>
              {t('tasks:listView')}
            </button>
            <button
              type="button"
              className={`tasks-view-toggle-btn${upcomingViewMode === 'calendar' ? ' is-active' : ''}`}
              onClick={() => setUpcomingViewMode('calendar')}
            >
              <span className="material-symbols-outlined" aria-hidden="true">calendar_month</span>
              {t('tasks:calendarView')}
            </button>
          </div>
        </div>

        {upcomingViewMode === 'calendar' ? (
          <TasksCalendarView
            tasks={calendarSourceTasks}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            selectedDay={selectedDay}
            onSelectDay={setSelectedDay}
            renderTaskCard={(task) => renderTaskCard(task)}
            canCreate={isCompanyAdmin(user)}
            onCreateForDay={onCreateForDay}
          />
        ) : sortedUpcomingTasks.length > 0 ? (
          renderTaskList(upcomingTasks, 'upcoming')
        ) : (
          <EmptyState
            icon="event"
            title={t('tasks:noUpcomingTasksTitle')}
            description={t('tasks:noUpcomingTasksDescription')}
          />
        )}
      </section>

      {(sortedCompletedTasks.length > 0 || allCompletedTasks.length > 0) && (
        <>
          <hr className="tasks-section-divider" aria-hidden="true" />
          <section
            id="tasks-section-completed"
            className="tasks-section"
            tabIndex={-1}
            aria-labelledby="tasks-section-completed-heading"
          >
            <div className="tasks-section-header tasks-section-header--with-toggle">
              <div>
                <h2 id="tasks-section-completed-heading" className="tasks-section-heading">
                  {t('tasks:done')}
                </h2>
                {sortedCompletedTasks.length > 0 && (
                  <p className="tasks-section-count">{sectionCountLabel(t, completedTasks.length)}</p>
                )}
              </div>
              {sortedCompletedTasks.length > 0 && (
                <span className="tasks-section-aside">{t('tasks:doneTodayLabel')}</span>
              )}
            </div>
            {showBillingControls && sortedCompletedTasks.length > 0 && (
              <div
                className="tasks-view-toggle tasks-billing-filter"
                role="group"
                aria-label={t('tasks:billingFilterAria')}
              >
                {BILLING_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`tasks-view-toggle-btn${billingFilter === filter.id ? ' is-active' : ''}`}
                    onClick={() => setBillingFilter(filter.id)}
                  >
                    {t(`tasks:${filter.labelKey}`)}
                  </button>
                ))}
              </div>
            )}
            {sortedCompletedTasks.length > 0 ? (
              completedTasks.length > 0 ? (
                renderTaskList(completedTasks, 'completed', { allowClaim: false })
              ) : (
                <EmptyState
                  icon="filter_alt"
                  title={t('tasks:billingFilterEmptyTitle')}
                  description={t('tasks:billingFilterEmptyDescription')}
                />
              )
            ) : (
              <EmptyState
                icon="check_circle"
                title={t('tasks:noCompletedTodayTitle')}
                description={t('tasks:noCompletedTodayDescription')}
              />
            )}
          </section>
        </>
      )}

      {allCompletedTasks.length > 0 && (
        <div className="tasks-older-link">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate('/task-export')}
          >
            {t('tasks:showOlderCompleted')}
          </button>
        </div>
      )}

      {includeRemoved && (
        <>
          <hr className="tasks-section-divider" aria-hidden="true" />
          <section className="tasks-section">
            <div className="tasks-section-header">
              <div>
                <h2 className="tasks-section-heading">{t('tasks:removedSection')}</h2>
                <p className="tasks-section-count">{sectionCountLabel(t, removedTasks.length)}</p>
              </div>
            </div>
            {removedTasks.length > 0 ? (
              renderTaskList(sortedRemovedTasks, 'removed', { allowClaim: false })
            ) : (
              <EmptyState
                icon="delete"
                title={t('tasks:noRemovedTasksTitle')}
                description={t('tasks:noRemovedTasksDescription')}
              />
            )}
          </section>
        </>
      )}
    </>
  );
}

export default TaskListSection;
